import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {defer, EMPTY, merge, Observable, of, Subject} from 'rxjs';
import {catchError, filter, map, share, switchMap, tap} from 'rxjs/operators';
import {extractListResponse, fetchAllPages, handleResponseError, ListResponse} from '../../common/api-helpers';
import {
    completePendingRequestIfExists,
    createPendingRequest,
    failPendingRequestIfExists,
    PendingRequest
} from '../../common/request-synchronization';
import {dependOn} from '../../common/rxjs/depend-on';
import {AccountType} from '../../enums/account-type';
import {Exchange, isExchangeWithSplitAccount, SMART_ACCOUNT_EXCHANGE_API_VALUE} from '../../enums/exchange';
import {SortDirection} from '../../enums/sort-direction';
import {UserRole} from '../../enums/user-role';
import {CommonListFilterQueryParamsFactory} from '../../factories/common-list-query-params.factory';
import {ListFilterFactory} from '../../factories/list-filter.factory';
import {ExchangeAccount, ExchangeAccountBase, ExchangeAccountId} from '../../models/exchange-account';
import {CommonListFilter} from '../../models/filters/common-list-filter';
import {SecurityKey} from '../../models/security-key';
import {AppConfigService} from '../../services/config.service';
import {AccountPermission, AccountPermissionRec} from '../account-permissions';
import {AuthService} from '../auth/auth.service';
import {CONSTANT_CASH_LEVERAGE} from '../leverage';
import {LastAccountService} from './last-account.service';

interface ExchangeAccountRegistrationResult {
    operationId: ExchangeAccountId; // accountId (ID of the created account)
    operationType: string; // e.g. "REGISTER_ACCOUNT"
    result: string; // e.g. "SUCCESS"
    text: string;
}

type AccountPendingRequest = PendingRequest<ExchangeAccount>;
type AccountsPendingRequest = PendingRequest<ExchangeAccount[]>;

const ALL_ACCOUNTS_REQUEST_FILTER: CommonListFilter = ListFilterFactory.createWithSearchFields(
    [],
    'id',
    SortDirection.DESC
);

interface AccountInfo {
    account: ExchangeAccount;
    accountBase: ExchangeAccountBase;
    leverage: number;
}

@Injectable()
export class AccountService {

    public static toAccountBase(account: ExchangeAccount, type?: AccountType): ExchangeAccountBase {
        return account && {
            id:   account.id,
            type: type || account.type
        };
    }

    public static canUserRoleManageAccounts(userRole: UserRole): boolean {
        return userRole === UserRole.TRADER_ADMIN;
    }

    public static rejectCustomAccounts(accounts: ExchangeAccount[]): ExchangeAccount[] {
        return accounts.filter(AccountService.isNotCustomAccount);
    }

    public static isCustomAccount(account: ExchangeAccount): boolean {
        return account.exchange === SMART_ACCOUNT_EXCHANGE_API_VALUE;
    }

    public static isNotCustomAccount(account: ExchangeAccount): boolean {
        return !AccountService.isCustomAccount(account);
    }

    private static isAccountActive(account: ExchangeAccount): boolean {
        return account.active;
    }

    private static filterActiveAccounts(accountsWithInactive: ExchangeAccount[]): ExchangeAccount[] {
        return accountsWithInactive.filter(AccountService.isAccountActive);
    }

    public readonly accounts$: Observable<ExchangeAccount[]>;

    protected accountAdminUrl: string = this.appConfigService.api['accountAdmin'];
    protected accountPermissionsUrl: string = this.appConfigService.api['accountPermissions'];

    protected readonly accountUrl: string = this.appConfigService.api['account'];

    private readonly accountByIdDict: {[accountId: string]: ExchangeAccount} = {};

    private pendingAllAccountsWithInactiveRequest: AccountsPendingRequest;
    private readonly pendingAccountRequestDict: {[accountId: string]: AccountPendingRequest} = {};

    private readonly accountsSubject$ = new Subject<ExchangeAccount[]>();

    private isCacheInitialized: boolean = false;

    private readonly inactiveAccountsCache: ExchangeAccount[] = [];

    constructor(
        protected httpClient: HttpClient,
        protected appConfigService: AppConfigService,
        private authService: AuthService,
        private lastAccountService: LastAccountService
    ) {
        this.accounts$ = this.createAccountsStream();

        this.listenToLogout();
    }

    public getAllAccounts(): Observable<ExchangeAccount[]> {
        return this.getAllAccountsWithInactive().pipe(
            map(AccountService.filterActiveAccounts),
            tap(accounts => this.cacheAllAccounts(accounts))
        );
    }

    public getAllAccountsWithFilter(listFilter: CommonListFilter): Observable<ExchangeAccount[]> {
        return fetchAllPages<ExchangeAccount>(
            pageListFilter => {
                let requestParams: HttpParams;

                if (pageListFilter) {
                    const params = CommonListFilterQueryParamsFactory.createQueryParamsAsString(pageListFilter);
                    requestParams = new HttpParams({fromString: params});
                }

                return this.httpClient.get<ListResponse<ExchangeAccount>>(
                    this.accountAdminUrl,
                    {params: requestParams}
                ).pipe(
                    catchError(handleResponseError)
                );
            },
            listFilter
        );
    }

    public getAllAccountsPermissions(listFilter: CommonListFilter): Observable<AccountPermissionRec[]> {
        return fetchAllPages<AccountPermissionRec>(
            pageListFilter => {
                let requestParams: HttpParams;

                if (pageListFilter) {
                    const params = CommonListFilterQueryParamsFactory.createQueryParamsAsString(pageListFilter);
                    requestParams = new HttpParams({fromString: params});
                }

                return this.httpClient.get<ListResponse<AccountPermissionRec>>(
                    this.accountPermissionsUrl,
                    {params: requestParams}
                );
            },
            listFilter
        ).pipe(
            catchError(handleResponseError)
        );
    }

    public getAllAccountsWithInactive(): Observable<ExchangeAccount[]> {
        const request$ = defer(() =>
            fetchAllPages<ExchangeAccount>(
                pageListFilter => this.createCustomAccountsRequest(pageListFilter),
                ALL_ACCOUNTS_REQUEST_FILTER
            ).pipe(
                tap(accounts => this.cacheAllAccounts(accounts))
            )
        );

        const pendingRequest = this.pendingAllAccountsWithInactiveRequest
            || createPendingRequest(
                request$,
                newPendingRequest => this.pendingAllAccountsWithInactiveRequest = newPendingRequest,
                () => this.pendingAllAccountsWithInactiveRequest = null
            );

        return pendingRequest.request$;
    }

    public getAllAccountsFromCache(): ExchangeAccount[] {
        return Object.values(this.accountByIdDict);
    }

    public getAccountsByExchangeFromCache(exchange: Exchange): ExchangeAccount[] {
        return this.getAllAccountsFromCache()
            .filter(account =>
                (exchange !== Exchange.SMART)
                    ? account.exchange === exchange
                    : account.exchange === SMART_ACCOUNT_EXCHANGE_API_VALUE
            );
    }

    public getAccountByIdFromCache(accountId: ExchangeAccountId): ExchangeAccount {
        return this.accountByIdDict[accountId];
    }

    public deleteAccount(accountId: ExchangeAccountId): Observable<any> {
        return this.httpClient.delete(`${this.accountUrl}/${accountId}`).pipe(
            tap(() => this.cacheAccountDelete(accountId)),
            tap(() => this.notifyAccountsUpdated()),
            catchError(handleResponseError)
        );
    }

    public createAccount(model: ExchangeAccount): Observable<ExchangeAccount> {
        return this.httpClient.post<ExchangeAccountRegistrationResult>(this.accountUrl, model).pipe(
            switchMap<ExchangeAccountRegistrationResult, Observable<ExchangeAccount>>(

                // TODO: [TT-597] What if the created account has become inactive before this line? Not handled for now.
                result => this.getAccountById(result.operationId)
            ),
            tap(() => this.notifyAccountsUpdated()),
            catchError(handleResponseError)
        );
    }

    public updateAccount(model: ExchangeAccount): Observable<ExchangeAccount> {

        /**
         * [HED-881] Some extra fields, that can be updated via special API.
         */
        const EXTRA_FIELDS_KEYS: Array<keyof ExchangeAccount> = [
            'publicKey',
            'privateKey',
            'apiPassword',
            'subAccountName'
        ];

        let hasExtraFieldsToUpdate: boolean = false;
        const extraFieldsToUpdate: Partial<ExchangeAccount> = EXTRA_FIELDS_KEYS.reduce(
            (acc: Partial<ExchangeAccount>, key: keyof ExchangeAccount) => {
                const value = model[key];

                if (value) {
                    hasExtraFieldsToUpdate = true;
                }

                (acc[key] as any) = value;

                return acc;
            },
            {}
        );

        const request$ = hasExtraFieldsToUpdate
            ? this.httpClient.post<ExchangeAccountRegistrationResult>(`${this.accountUrl}/update`, {
                accountId:      model.id,
                displayName:    model.displayName,
                userId:         model.userId,
                ...extraFieldsToUpdate
            }).pipe(
                switchMap(() => this.getAccountById(model.id))
            )
            : this.httpClient.put<ExchangeAccount>(`${this.accountUrl}/${model.id}`, model.displayName);

        return request$.pipe(
            tap(account => this.cacheAccount(account)),
            tap(() => this.notifyAccountsUpdated()),
            catchError(handleResponseError)
        );
    }

    public getLastAccountBaseByExchange(exchange: Exchange): ExchangeAccountBase {
        const accountBase = this.lastAccountService.getLastAccountByExchange(exchange);
        const account = accountBase && this.getAccountByIdFromCache(accountBase.id);

        return account && AccountService.toAccountBase(account, accountBase && accountBase.type);
    }

    public getDefaultAccountBaseByExchangeFromCache(exchange: Exchange): ExchangeAccountBase {
        return AccountService.toAccountBase(this.getDefaultAccountByExchangeFromCache(exchange));
    }

    public getDefaultAccountByExchangeFromCache(exchange: Exchange): ExchangeAccount {
        const lastAccountBase = this.getLastAccountBaseByExchange(exchange);

        const exchangeLastAccount = lastAccountBase
            && this.getAccountByIdFromCache(lastAccountBase.id);

        return exchangeLastAccount || this.getAccountsByExchangeFromCache(exchange)[0];
    }

    // tslint:disable:cyclomatic-complexity
    public validateAccountBaseOrDefaultFromCache(
        accountBase: ExchangeAccountBase,
        exchange?: Exchange
    ): ExchangeAccountBase {
        const getDefaultAccount = () => exchange
            ? this.getDefaultAccountByExchangeFromCache(exchange)
            : this.getAllAccountsFromCache()[0];

        const origAccountType = accountBase && accountBase.type;

        let correctAccount: ExchangeAccount = accountBase && this.getAccountByIdFromCache(accountBase.id);
        let accountType: AccountType;

        const isAccountBaseCorrect = !!correctAccount && (!exchange || correctAccount.exchange === exchange);

        if (!isAccountBaseCorrect) { // accountBase is not correct
            correctAccount = getDefaultAccount();

            if (correctAccount) {

                // Keep account type from orig account base, if possible
                accountType = isExchangeWithSplitAccount(correctAccount.exchange) && origAccountType
                    ? origAccountType
                    : correctAccount.type;
            }
        } else if (correctAccount) { // accountBase is correct
            accountType = !isExchangeWithSplitAccount(correctAccount.exchange)
                ? correctAccount.type
                : origAccountType || correctAccount.type;
        }

        return correctAccount && {
            id:   correctAccount.id,
            type: accountType || correctAccount.type
        };
    }

    public getDefaultAccountInfoByParams(params: {
        securityKeys?: SecurityKey[];
        exchange?: Exchange;
        permission?: AccountPermission;
    }): AccountInfo | undefined {
        let result: AccountInfo;

        /**
         * Get the last used account.
         */

        const lastAccountRec = params.exchange
            ? this.lastAccountService.getLastAccountByExchange(params.exchange)
            : (
                this.lastAccountService.getLastAccount(params.securityKeys)
                || this.lastAccountService.getLastAccountByExchange(_.last(params.securityKeys).securityExchange)
            );

        const lastAccount = lastAccountRec && this.getAccountByIdFromCache(lastAccountRec.id);

        if (lastAccount) {
            result = {
                account:     lastAccount,
                accountBase: {
                    id:   lastAccountRec.id,
                    type: lastAccountRec.type
                },
                leverage:    lastAccountRec.leverage
            };
        }

        /**
         * Check permission of the last used account.
         */
        if (result && params.permission) {
            if (!this.hasPermissionOnAccount(result.account, params.permission)) {
                result = undefined;
            }
        }

        /**
         * Get default account, if there is not the last used account,
         * that matches the parameters.
         */
        if (!result) {
            const account = this.getAllAccountsFromCache()
                .find(itAccount => {
                    let isMatched: boolean = true;

                    if (params.exchange) {
                        isMatched = isMatched && itAccount.exchange === params.exchange;
                    } else if (params.securityKeys) {
                        const exchangeFromSecurityKey = _.last(params.securityKeys)
                            .securityExchange;

                        isMatched = isMatched && itAccount.exchange === exchangeFromSecurityKey;
                    }

                    if (params.permission) {
                        isMatched = isMatched && this.hasPermissionOnAccount(itAccount, params.permission);
                    }

                    return isMatched;
                });

            if (account) {
                result = {
                    account,
                    accountBase: {
                        id:   account.id,
                        type: account.type
                    },
                    leverage:    (account.type === AccountType.MARGIN)
                                     ? undefined
                                     : CONSTANT_CASH_LEVERAGE
                };
            }
        }

        return result;
    }

    public validateAccountInfoOrDefaultFromCache(
        accountBase: ExchangeAccountBase,
        leverage: number,
        params: {
            securityKeys?: SecurityKey[];
            exchange?: Exchange;
            permission?: AccountPermission;
        }
    ): AccountInfo | undefined {
        const origAccountType = accountBase && accountBase.type;

        let correctAccount: ExchangeAccount = accountBase && this.getAccountByIdFromCache(accountBase.id);
        let accountType: AccountType;

        const isAccountBaseCorrect = !!correctAccount
            && (!params.exchange || correctAccount.exchange === params.exchange)
            && (
                !params.securityKeys || !params.securityKeys.length
                || correctAccount.exchange === _.last(params.securityKeys).securityExchange
            );

        if (!isAccountBaseCorrect) { // accountBase is not correct
            const defaultAccountInfo = this.getDefaultAccountInfoByParams(params);
            correctAccount = defaultAccountInfo && defaultAccountInfo.account;

            if (correctAccount) {

                // Keep account type from orig account base, if possible
                accountType = isExchangeWithSplitAccount(correctAccount.exchange)
                    ? origAccountType || defaultAccountInfo.accountBase.type
                    : correctAccount.type;

                leverage = defaultAccountInfo.leverage;
            }
        } else if (correctAccount) { // accountBase is correct
            accountType = !isExchangeWithSplitAccount(correctAccount.exchange)
                ? correctAccount.type
                : origAccountType || correctAccount.type;
        }

        return correctAccount
            ? {
                account:     correctAccount,
                accountBase: {
                    id:   correctAccount.id,
                    type: accountType || correctAccount.type
                },
                leverage
            }
            : undefined;
    }

    public getAllInactiveAccountsFromCache(): ExchangeAccount[] {
        return this.inactiveAccountsCache.slice();
    }

    public cacheInactiveAccount(account: ExchangeAccount): void {
        const knowInactiveAccount = this.inactiveAccountsCache.find(knownAccount => knownAccount.id === account.id);

        if (knowInactiveAccount) {
            Object.assign(knowInactiveAccount, account);
        } else {
            this.cacheAccountDelete(account.id);

            this.inactiveAccountsCache.push(account);
        }
    }

    public notifyAccountsUpdated(): void {
        this.accountsSubject$.next(this.getAllAccountsFromCache());
    }

    public updateAccountPermission(accountPermissionRec: AccountPermissionRec): Observable<any> {
        return this.httpClient.post<any>(this.accountPermissionsUrl, accountPermissionRec).pipe(
            tap(() => this.notifyAccountsUpdated()),
            catchError(handleResponseError)
        );
    }

    public hasPermissionOnAccount(
        account: ExchangeAccount,
        permission: AccountPermission = AccountPermission.WRITE
    ): boolean {
        return account
            ? account.permission === permission
            : false;
    }

    public hasPermissionOnAccountId(
        accountId: ExchangeAccountId,
        permission: AccountPermission = AccountPermission.WRITE
    ): boolean {
        const account = accountId && this.getAccountByIdFromCache(accountId);

        return this.hasPermissionOnAccount(account, permission);
    }

    protected createCustomAccountsRequest(accountsFilter: CommonListFilter): Observable<ListResponse<ExchangeAccount>> {
        let requestParams: HttpParams;

        if (accountsFilter) {
            const params = CommonListFilterQueryParamsFactory.createQueryParamsAsString(accountsFilter);
            requestParams = new HttpParams({fromString: params});
        }

        return this.httpClient.get<ListResponse<ExchangeAccount>>(this.accountUrl, {params: requestParams}).pipe(
            map(extractListResponse),
            catchError(handleResponseError)
        );
    }

    protected createAccountsStream(): Observable<ExchangeAccount[]> {
        const knownAccounts$: Observable<ExchangeAccount[]> = defer(() =>
            this.isCacheInitialized
                ? of(this.getAllAccountsFromCache())
                : EMPTY
        );

        const initialFetching$ = defer(() =>
            this.isCacheInitialized
                ? EMPTY
                : this.getAllAccounts()
        ).pipe(
            share()
        );

        return this.authService.userRole$.pipe(
            switchMap(userRole => userRole
                ? merge(
                    knownAccounts$,
                    this.accountsSubject$
                ).pipe(
                    dependOn(initialFetching$)
                )
                : of([])
            )
        );
    }

    private cacheAccount(account: ExchangeAccount): void {
        const accountId = account.id;

        if (AccountService.isAccountActive(account)) {
            this.accountByIdDict[accountId] = account;

            completePendingRequestIfExists(this.pendingAccountRequestDict[accountId], account);

            this.isCacheInitialized = true;
        } else {
            this.cacheInactiveAccount(account);
        }
    }

    private cacheAccounts(accounts: ExchangeAccount[]): void {
        accounts.forEach(account => this.cacheAccount(account));
    }

    private cacheAllAccounts(allAccounts: ExchangeAccount[]): void {

        // Remove old accounts from cache (silently).
        _.differenceBy(
            this.getAllAccountsFromCache(),
            allAccounts,
            account => account.id
        )
            .forEach(account => this.cacheAccountDelete(account.id));

        // Remove old accounts from inactive accounts cache
        if (this.inactiveAccountsCache.length) {
            _.differenceBy(
                this.inactiveAccountsCache,
                allAccounts.filter(account => !account.active),
                account => account.id
            ).forEach(account => _.pull(this.inactiveAccountsCache, account));
        }

        this.cacheAccounts(allAccounts);

        this.notifyAccountsUpdated();
    }

    private cacheAccountDelete(accountId: ExchangeAccountId): void {
        failPendingRequestIfExists(this.pendingAccountRequestDict[accountId]);

        delete this.accountByIdDict[accountId];
    }

    private clearAccounts(): void {
        failPendingRequestIfExists(this.pendingAllAccountsWithInactiveRequest);

        Object.values(this.accountByIdDict)
            .forEach(account => this.cacheAccountDelete(account.id));

        this.notifyAccountsUpdated();

        this.isCacheInitialized = false;
    }

    private listenToLogout(): void {
        this.authService.userRole$.pipe(
            filter(userRole => !userRole)
        ).subscribe(() => this.clearAccounts());
    }

    private getAccountById(accountId: ExchangeAccountId): Observable<ExchangeAccount> {
        const request$ = this.httpClient.get<ExchangeAccount>(`${this.accountUrl}/${accountId}`).pipe(
            tap(account => this.cacheAccount(account)),
            tap(() => this.notifyAccountsUpdated()),
            catchError(handleResponseError)
        );
        const pendingRequest = this.pendingAccountRequestDict[accountId]
            || createPendingRequest(
                request$,
                newPendingRequest => this.pendingAccountRequestDict[accountId] = newPendingRequest,
                () => delete this.pendingAccountRequestDict[accountId]
            );

        return pendingRequest.request$;
    }

}
